import { useParams, Navigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicNav } from "@/components/PublicNav";
import { SeoJsonLd } from "@/components/SeoJsonLd";
import { useSeo, SITE_URL } from "@/lib/seo";
import { REGIOS, REGIO_LIST } from "@/lib/regios";
import { MapPin, ArrowRight, ClipboardList, Users, FileText, CheckCircle2 } from "lucide-react";

export default function RegioLanding() {
  const { slug } = useParams();
  const regio = REGIOS[slug];

  useSeo({
    title: regio?.metaTitle,
    description: regio?.metaDescription,
    path: `/regio/${slug}`,
    keywords: regio?.keywords,
  });

  if (!regio) return <Navigate to="/" replace />;

  const url = `${SITE_URL}/regio/${regio.slug}`;
  const extraSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Ramen- en deurenmontage",
      url,
      provider: { "@type": "Organization", name: "Work4you", url: SITE_URL },
      areaServed: { "@type": regio.type, name: regio.naam },
      description: regio.metaDescription,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: `Ramen en deuren monteren in ${regio.naam}`, item: url },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="regio-page">
      <SeoJsonLd extra={extraSchema} />
      <PublicNav />

      {/* Hero */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <p className="inline-flex items-center gap-2 label-caps text-orange-400 mb-4">
            <MapPin className="w-4 h-4" /> Regio {regio.naam}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5" data-testid="regio-h1">
            Ramen en deuren monteren in {regio.naam}
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-8">
            {regio.intro[0]}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/offerte-aanvragen">
              <Button data-testid="regio-offerte-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold px-7 h-12 w-full sm:w-auto">
                <FileText className="w-4 h-4 mr-2" /> Vraag gratis offerte aan
              </Button>
            </Link>
            <Link to="/beschikbare_monteurs">
              <Button data-testid="regio-monteurs-btn" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-slate-950 rounded-sm font-bold px-7 h-12 w-full sm:w-auto">
                <Users className="w-4 h-4 mr-2" /> Bekijk professionals
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <p className="text-slate-600 leading-relaxed mb-10 max-w-3xl">{regio.intro[1]}</p>

        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {[
            { icon: FileText, t: "Particulieren", d: `Vraag gratis een prijsofferte aan voor ramen en deuren en vergelijk geverifieerde bedrijven in ${regio.naam}.`, to: "/offerte-aanvragen", cta: "Offerte aanvragen" },
            { icon: ClipboardList, t: "Bedrijven", d: "Plaats opdrachten of doorzoek de professionalsgids en vind snel de juiste vakman voor je project.", to: "/opdrachten", cta: "Bekijk opdrachten" },
            { icon: Users, t: "Professionals", d: "Zelfstandig raam- of kozijnenmonteur? Vind opdrachten in je regio en stel je beschikbaar.", to: "/beschikbare_monteurs", cta: "Beschikbare professionals" },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-slate-200 rounded-md p-7 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
              <div className="w-11 h-11 bg-slate-950 flex items-center justify-center rounded-sm mb-4">
                <c.icon className="w-5 h-5 text-orange-500" />
              </div>
              <h2 className="font-display text-lg font-bold text-slate-950 mb-2">{c.t}</h2>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">{c.d}</p>
              <Link to={c.to} className="inline-flex items-center gap-1 text-orange-600 font-semibold text-sm hover:gap-2 transition-all">
                {c.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 mb-3">Actief in heel {regio.naam}</h2>
            <p className="text-slate-600 text-sm mb-4">
              Vind vakmensen voor ramen- en deurenmontage in en rond:
            </p>
            <div className="flex flex-wrap gap-2">
              {regio.steden.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-sm px-3 py-1 text-sm">
                  <MapPin className="w-3.5 h-3.5 text-orange-500" /> {s}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 mb-3">Wat we monteren</h2>
            <ul className="text-slate-600 text-sm space-y-2">
              {["PVC, aluminium en houten ramen", "Raamkozijnen & deurkozijnen", "Schuiframen & voordeuren", "Nieuwe ramen en deuren bij renovatie", "Garagepoorten, rolluiken, screens & Velux"].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" /> {x}
                </li>
              ))}
            </ul>
            <p className="text-slate-500 text-sm mt-4">
              Van bekende merken als Kömmerling, Schüco, Aliplast, Aluplast, Deceuninck, Veka, Meranti en Aluprof.
            </p>
          </div>
        </div>

        {/* Cross-links naar andere regio's */}
        <div className="mt-14 pt-8 border-t border-slate-200">
          <p className="text-sm font-semibold text-slate-500 mb-3">Andere regio's</p>
          <div className="flex flex-wrap gap-2">
            {REGIO_LIST.filter((r) => r.slug !== regio.slug).map((r) => (
              <Link key={r.slug} to={`/regio/${r.slug}`} data-testid={`regio-link-${r.slug}`}
                className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-sm px-3 py-1.5 text-sm text-slate-700 hover:border-orange-400 hover:text-orange-600 transition-colors">
                <MapPin className="w-3.5 h-3.5" /> Ramen monteren in {r.naam}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-slate-500 text-sm">
          <span className="font-display font-bold text-white">Work4you</span>
          <span>© 2026 — Ramen &amp; deuren montage marktplaats</span>
        </div>
      </footer>
    </div>
  );
}
